import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import type { CarReport, VehicleDefectProfile } from '../api/types'
import { useCarReport } from '../api/useCarReport'
import { useVehicleDefectProfile } from '../api/useVehicleDefectProfile'
import { DocumentMeta } from '../components/DocumentMeta'
import { Icon } from '../components/Icon'
import { CHECKLIST, personalNotes } from '../domain/checklist'
import { formatPlate, parsePlate } from '../domain/licensePlate'
import { checklistMeta } from '../domain/meta'
import { vehicleName } from '../domain/format'

/** What the report page hands over when its button opens this page, so nothing is fetched twice. */
export interface ChecklistHandover {
  report: CarReport
  defectProfile?: VehicleDefectProfile
}

const ARTICLE_URL = 'https://www.autochecken.com/occasion-kopen/checklist/'

/**
 * The buying checklist as something to fill in: tick the points, note what you saw, print it.
 *
 * `/checklist` is the blank one; `/checklist/{plate}` adds what the report already answers for
 * that car beside the points. Coming from a report, the report travels in the router state and is
 * not requested again; a direct link fetches it like the report page does.
 */
export function ChecklistPage() {
  const { plate: rawPlate } = useParams<{ plate: string }>()
  const handover = useLocation().state as ChecklistHandover | null

  if (rawPlate === undefined) {
    return <ChecklistSheet key="blank" />
  }

  const parsed = parsePlate(rawPlate)
  if (!parsed) {
    return <Navigate to="/checklist" replace />
  }

  if (parsed.compact !== rawPlate) {
    return <Navigate to={`/checklist/${parsed.compact}`} replace state={handover} />
  }

  if (handover?.report.licensePlateCompact === parsed.compact) {
    return <ChecklistSheet key={parsed.compact} plate={parsed.compact} report={handover.report} defectProfile={handover.defectProfile} />
  }

  return <FetchedChecklist key={parsed.compact} plate={parsed.compact} />
}

/** A checklist opened from a link rather than from the report: the car's data is fetched here. */
function FetchedChecklist({ plate }: { plate: string }) {
  const state = useCarReport(plate)
  const loaded = state.status === 'loaded'
  const defectProfile = useVehicleDefectProfile(plate, loaded)

  // A plate the RDW cannot answer for still gets its checklist, just without the filled-in notes:
  // the points are worth ticking whether or not the register could be reached.
  return (
    <ChecklistSheet
      plate={plate}
      report={loaded ? state.report : undefined}
      defectProfile={defectProfile}
      loading={state.status === 'loading'}
    />
  )
}

/** Everything a reader filled in, kept per plate in this browser only. */
interface ChecklistEntries {
  checked: number[]
  notes: Record<number, string>
  price: string
  date: string
  remarks: string
}

const EMPTY: ChecklistEntries = { checked: [], notes: {}, price: '', date: '', remarks: '' }

/**
 * Per plate, so someone comparing two cars keeps two checklists. Browser storage and nothing else:
 * no account, nothing on a server, and a private window or blocked storage simply starts empty.
 */
function storageKey(plate: string | undefined) {
  return `autochecken.checklist.${plate ?? 'zonder-kenteken'}`
}

function loadEntries(plate: string | undefined): ChecklistEntries {
  try {
    const stored = window.localStorage.getItem(storageKey(plate))
    return stored ? { ...EMPTY, ...(JSON.parse(stored) as Partial<ChecklistEntries>) } : EMPTY
  } catch {
    return EMPTY
  }
}

function saveEntries(plate: string | undefined, entries: ChecklistEntries) {
  try {
    if (entries === EMPTY) {
      window.localStorage.removeItem(storageKey(plate))
    } else {
      window.localStorage.setItem(storageKey(plate), JSON.stringify(entries))
    }
  } catch {
    // Storage blocked or full: the checklist still works, it just is not remembered.
  }
}

function ChecklistSheet({
  plate,
  report,
  defectProfile,
  loading = false,
}: {
  plate?: string
  report?: CarReport
  defectProfile?: VehicleDefectProfile
  loading?: boolean
}) {
  const [entries, setEntries] = useState<ChecklistEntries>(() => loadEntries(plate))
  const notes = report ? personalNotes(report, defectProfile) : new Map<number, string>()
  const total = CHECKLIST.reduce((sum, phase) => sum + phase.points.length, 0)
  const name = report ? vehicleName(report.identity.brand, report.identity.tradeName) : undefined

  // Each plate gets its own instance (the callers key it by plate), so the entries read on mount are
  // always this plate's and a save can never land under another plate's key.
  useEffect(() => saveEntries(plate, entries), [plate, entries])

  const update = (change: Partial<ChecklistEntries>) => setEntries((current) => ({ ...current, ...change }))
  const toggle = (number: number) =>
    update({
      checked: entries.checked.includes(number)
        ? entries.checked.filter((n) => n !== number)
        : [...entries.checked, number],
    })

  // The browser proposes the document title as the PDF's file name, so it carries the plate.
  const print = () => {
    const title = document.title
    if (plate) document.title = `Checklist occasion kopen ${formatPlate(plate)}`
    window.print()
    document.title = title
  }

  const clear = () => {
    if (window.confirm('Alles wat je op deze checklist hebt ingevuld wissen?')) {
      setEntries(EMPTY)
    }
  }

  return (
    <main className="container checklist-page" id="main">
      <DocumentMeta {...checklistMeta(plate ? formatPlate(plate) : undefined, name)} />

      <header className="checklist-page__intro">
        <div className="checklist-sheet__header">
          <h1 className="checklist-sheet__title">Checklist occasion kopen</h1>
          <div className="checklist-sheet__brand">
            AutoChecken
            <small>25 controles in vijf fases</small>
          </div>
        </div>

        <p className="checklist-page__lead">
          Vink af wat je hebt gecontroleerd en schrijf erbij wat je opvalt. Wat je invult, blijft alleen
          in deze browser bewaard. Klaar? Print de checklist of sla hem op als PDF.
          {' '}
          <a href={ARTICLE_URL}>Uitleg bij elk punt</a>.
        </p>

        {plate && (
          <p className="checklist-page__car">
            {loading
              ? `Gegevens van ${formatPlate(plate)} worden opgehaald…`
              : name
                ? <>Ingevuld met de RDW-gegevens van deze <strong>{name}</strong>. <Link to={`/kenteken/${plate}`}>Naar het rapport</Link></>
                : `De RDW-gegevens van ${formatPlate(plate)} konden niet worden opgehaald; de checklist werkt gewoon.`}
          </p>
        )}

        <div className="checklist-page__actions">
          <button className="button" type="button" onClick={print}>
            <Icon name="print" size={16} />
            Print of sla op als PDF
          </button>
          <button className="button button--quiet" type="button" onClick={clear}>
            Leegmaken
          </button>
          <span className="checklist-page__progress" aria-live="polite">
            {entries.checked.length} van {total} afgevinkt
          </span>
        </div>
      </header>

      {!plate && <PlateForm />}

      <div className="checklist-sheet__fields">
        <label>
          Kenteken
          {plate ? <strong className="checklist-sheet__plate">{formatPlate(plate)}</strong> : <span className="checklist-sheet__blank" />}
        </label>
        <label>
          Vraagprijs
          <input
            type="text"
            inputMode="numeric"
            value={entries.price}
            onChange={(event) => update({ price: event.target.value })}
            placeholder="€"
          />
        </label>
        <label>
          Datum bezichtiging
          <input type="date" value={entries.date} onChange={(event) => update({ date: event.target.value })} />
        </label>
      </div>

      <div className="checklist-sheet__columns">
        {CHECKLIST.map((phase, index) => (
          <section className="checklist-sheet__phase" key={phase.title}>
            <h2 className="checklist-sheet__phase-title">
              <span className="checklist-sheet__phase-number">{index + 1}</span>
              {phase.title}
            </h2>
            {phase.hint && <p className="checklist-sheet__hint">{phase.hint}</p>}
            <ul className="checklist-sheet__points">
              {phase.points.map((point) => {
                const note = notes.get(point.number)
                const own = entries.notes[point.number] ?? ''
                return (
                  <li className="checklist-sheet__point" key={point.number}>
                    <input
                      className="checklist-sheet__box"
                      type="checkbox"
                      id={`punt-${point.number}`}
                      checked={entries.checked.includes(point.number)}
                      onChange={() => toggle(point.number)}
                    />
                    <b className="checklist-sheet__number">{point.number}</b>
                    <div>
                      <label htmlFor={`punt-${point.number}`}>{point.title}</label>
                      {point.detail && <small className="checklist-sheet__detail">{point.detail}</small>}
                      {note && <small className="checklist-sheet__note">{note}</small>}
                      <input
                        className={`checklist-sheet__own${own ? '' : ' checklist-sheet__own--empty'}`}
                        type="text"
                        value={own}
                        onChange={(event) => update({ notes: { ...entries.notes, [point.number]: event.target.value } })}
                        placeholder="Notitie"
                        aria-label={`Notitie bij punt ${point.number}`}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}

        <section className="checklist-sheet__phase checklist-sheet__remarks">
          <h2 className="checklist-sheet__phase-title">
            <label htmlFor="opmerkingen">Opmerkingen</label>
          </h2>
          <textarea
            id="opmerkingen"
            rows={6}
            value={entries.remarks}
            onChange={(event) => update({ remarks: event.target.value })}
          />
        </section>
      </div>

      <footer className="checklist-sheet__footer">
        <span>Uitleg bij elk punt: autochecken.com/occasion-kopen/checklist/</span>
        <span>
          {report ? 'Ingevuld met de gegevens van de RDW op ' : 'Gratis kentekencheck: '}
          kentekencheck.autochecken.com
        </span>
      </footer>
    </main>
  )
}

/** On the blank checklist: a plate turns it into the checklist for that car. */
function PlateForm() {
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [invalid, setInvalid] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const parsed = parsePlate(value)
    if (parsed) {
      navigate(`/checklist/${parsed.compact}`)
    } else {
      setInvalid(true)
    }
  }

  return (
    <form className="checklist-page__plate-form" onSubmit={submit}>
      <label htmlFor="checklist-kenteken">
        Heb je het kenteken? Dan zetten we de APK, het tellerstandoordeel, terugroepacties en de zwakke
        plekken van het model er meteen bij.
      </label>
      <div className="checklist-page__plate-row">
        <input
          id="checklist-kenteken"
          type="text"
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
            setInvalid(false)
          }}
          placeholder="XX-123-X"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={invalid}
        />
        <button className="button" type="submit">
          Invullen
        </button>
      </div>
      {invalid && <p className="checklist-page__error">Dit is geen geldig Nederlands kenteken.</p>}
    </form>
  )
}
